import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import QueryString from 'qs';
import { useEffect, useMemo } from 'react';

import CampaignLayout from '@/components/campaign';
import DiscountLandingWrapper from '@/components/discountLanding/DiscountLandingWrapper';
import Footer from '@/components/footer/FooterWrapper';
import Header from '@/components/header/Header';
import Layout from '@/components/layout/LayoutWrapper';
import { usePrices } from '@/hooks/prices/usePrices';
import { InitRequest, useInitState, useUserState } from '@/hooks/store';
import { getCampaign } from '@/server/initState/getCampaign';
import { Campaign } from '@/store/campaign/types';

const getCouponQueryParam = (campaign: Campaign) => {
	if (!campaign.coupon) return {};

	return campaign.plan === 'lifetime'
		? { lifetime_coupon: campaign.coupon }
		: { yearly_coupon: campaign.coupon };
};

const Campaigns: React.FC<{
	campaign: Campaign;
}> = ({ campaign }) => {
	const user = useUserState();
	const { query, ...router } = useRouter();
	const couponQueryParam = getCouponQueryParam(campaign);

	const campaignWithQueryParams = useMemo(
		() => ({
			...campaign,
			...couponQueryParam,
			vanity_url: `/${campaign.slug}`,
		}),
		[campaign, couponQueryParam],
	);

	usePrices({
		coupon: campaign.coupon,
		...couponQueryParam,
	});

	useInitState({
		requests: [InitRequest.USER, InitRequest.DEVICE],
		awaitPostDevice: false,
		campaign: { ...campaignWithQueryParams, slug: '/campaigns' },
	});

	const isPodcastCampaign = campaign.utm_medium === 'podcast';
	const isSubscribed = user?.subscription?.valid ?? false;
	const welcomeText = isSubscribed
		? campaign.subscribed_banner_string
		: campaign.not_subscribed_banner_string;

	// We need to append the query paramaters to the URL so that
	// these props are tracked when the user navigates to the next page
	useEffect(() => {
		if (query.slug !== campaign.slug) {
			// eslint-disable-next-line no-console
			router.push(`/${campaign.slug}?${QueryString.stringify(campaignWithQueryParams)}`).catch(console.error);
		}
	}, [campaign.slug, campaignWithQueryParams, query.slug, router]);

	return (
		<Layout pageName="campaigns" title="Calm - The #1 App for Meditation and Sleep">
			{isPodcastCampaign ? (
				<DiscountLandingWrapper welcomeText={welcomeText} />
			) : (
				<>
					<Header />
					<CampaignLayout campaign={campaign} />
				</>
			)}
			<Footer />
		</Layout>
	);
};

export default Campaigns;

export const getStaticProps: GetStaticProps = async ({ params }) => {
	try {
		if (typeof params?.campaignSlug === 'string') {
			const campaign = await getCampaign(params.campaignSlug);

			return {
				props: {
					campaign,
				},
				revalidate: 60 * 60 * 6, // 6 hours,
			};
		}
	} catch (err) {
		return {
			notFound: true,
		};
	}

	return {
		notFound: true,
	};
};

export const getStaticPaths: GetStaticPaths = () => {
	return { paths: [], fallback: 'blocking' };
};
